import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnit } from 'effector-react';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepButton,
  Stack,
  TextField,
  Button,
  Alert,
  Divider,
  Chip,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import { residentialComplexesApi } from '../api/residentialComplexes';
import { buildingsApi } from '../api/buildings';
import { buildingCorpsApi } from '../api/buildingCorps';
import { bulkApi } from '../api/bulk';
import { gatewaysApi } from '../api/gateways';
import { downloadBlobAsFile } from '../utils/download';
import { fetchResidentialComplexesFx } from '../store/complexes';
import { fetchBuildingsByComplexFx } from '../store/buildings';
import { fetchCorpsByBuildingFx } from '../store/buildingCorps';
import { $isSettlementTenant, $objectKind } from '../store';
import AddressAutocompleteField from '../components/AddressAutocompleteField';
import { showSoftSuccess } from '../utils/softSuccess';
import { complexKindLabel, getObjectKindLabels, normalizeComplexKind } from '../utils/complexKind';

const mduSteps = [
  { key: 'complex', label: 'ЖК' },
  { key: 'building', label: 'Дом' },
  { key: 'corps', label: 'Корпуса' },
  { key: 'import', label: 'Импорт' },
  { key: 'gateways', label: 'Шлюзы' },
];

const settlementSteps = [
  { key: 'complex', label: 'Посёлок' },
  { key: 'building', label: 'Дом' },
  { key: 'corps', label: 'Участки' },
  { key: 'import', label: 'Импорт' },
  { key: 'gateways', label: 'Шлюзы' },
];

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return value;
  }
};

const statusColor = (status) => {
  if (status === 'UP') return 'success';
  if (status === 'DEGRADED') return 'warning';
  if (status === 'DOWN') return 'error';
  return 'default';
};

const OnboardingPage = ({ showDismiss = false, onDismiss, onComplete } = {}) => {
  const navigate = useNavigate();
  const [objectKind, isSettlementTenant] = useUnit([$objectKind, $isSettlementTenant]);
  const kind = normalizeComplexKind(objectKind);
  const kindLabel = complexKindLabel(kind);
  const labels = useMemo(
    () => getObjectKindLabels(isSettlementTenant),
    [isSettlementTenant]
  );
  const steps = isSettlementTenant ? settlementSteps : mduSteps;
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});

  const [complexForm, setComplexForm] = useState({
    name: '',
    address: '',
    latitude: null,
    longitude: null,
    complex_kind: 'mdu',
  });

  useEffect(() => {
    setComplexForm((prev) => ({ ...prev, complex_kind: kind }));
  }, [kind]);
  const [complexErrors, setComplexErrors] = useState({ name: '', address: '' });
  const [complexError, setComplexError] = useState('');
  const [complexSuccess, setComplexSuccess] = useState('');
  const [complexLoading, setComplexLoading] = useState(false);
  const [complex, setComplex] = useState(null);

  const [buildingForm, setBuildingForm] = useState({
    name: '',
    address: '',
    latitude: null,
    longitude: null,
  });
  const [buildingErrors, setBuildingErrors] = useState({ name: '' });
  const [buildingError, setBuildingError] = useState('');
  const [buildingSuccess, setBuildingSuccess] = useState('');
  const [buildingLoading, setBuildingLoading] = useState(false);
  const [building, setBuilding] = useState(null);

  const [corpsDraft, setCorpsDraft] = useState('');
  const [corpsList, setCorpsList] = useState([]);
  const [corpsError, setCorpsError] = useState('');
  const [corpsSuccess, setCorpsSuccess] = useState('');
  const [corpsLoading, setCorpsLoading] = useState(false);
  const [createdCorps, setCreatedCorps] = useState([]);

  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkError, setBulkError] = useState('');
  const [bulkSuccess, setBulkSuccess] = useState('');
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [bulkSkipped, setBulkSkipped] = useState(false);

  const [gateways, setGateways] = useState([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(false);
  const [gatewaysError, setGatewaysError] = useState('');

  const totalSteps = steps.length;

  const completedCount = useMemo(
    () => Object.values(completed).filter(Boolean).length,
    [completed]
  );

  const stepProgress = Math.round((completedCount / totalSteps) * 100);

  const markCompleted = (stepIndex) => {
    setCompleted((prev) => ({ ...prev, [stepIndex]: true }));
  };

  const canStepTo = useCallback(
    (stepIndex) => {
      if (stepIndex === 0) return true;
      return Array.from({ length: stepIndex }).every((_, idx) => completed[idx]);
    },
    [completed]
  );

  const handleStep = (index) => {
    if (canStepTo(index)) {
      setActiveStep(index);
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const validateComplex = (data) => {
    const next = { name: '', address: '' };
    if (!data.name?.trim()) next.name = 'Название обязательно';
    if (!data.address?.trim()) next.address = 'Адрес обязателен';
    return next;
  };

  const validateBuilding = (data) => {
    const next = { name: '' };
    if (!data.name?.trim()) next.name = 'Название обязательно';
    return next;
  };

  const handleCreateComplex = async () => {
    const validation = validateComplex(complexForm);
    setComplexErrors(validation);
    if (Object.values(validation).some(Boolean)) return;

    setComplexLoading(true);
    setComplexError('');
    setComplexSuccess('');
    try {
      const created = await residentialComplexesApi.createResidentialComplex({
        name: complexForm.name.trim(),
        address: complexForm.address.trim(),
        latitude: complexForm.latitude,
        longitude: complexForm.longitude,
        complex_kind: kind,
      });
      setComplex(created);
      setComplexSuccess(`${kindLabel} добавлен`);
      showSoftSuccess(`${kindLabel} добавлен`);
      markCompleted(0);
      setActiveStep(1);
      fetchResidentialComplexesFx().catch(() => {});
    } catch (err) {
      setComplexError(
        err?.response?.data?.detail ||
          err?.message ||
          `Не удалось создать ${kindLabel.toLowerCase()}`
      );
    } finally {
      setComplexLoading(false);
    }
  };

  const handleCreateBuilding = async () => {
    if (!complex?.id) {
      setBuildingError('Сначала создайте ЖК');
      return;
    }
    const validation = validateBuilding(buildingForm);
    setBuildingErrors(validation);
    if (Object.values(validation).some(Boolean)) return;

    setBuildingLoading(true);
    setBuildingError('');
    setBuildingSuccess('');
    try {
      const created = await buildingsApi.createBuilding({
        name: buildingForm.name.trim(),
        address: buildingForm.address?.trim() || undefined,
        latitude: buildingForm.latitude,
        longitude: buildingForm.longitude,
        residential_complex_id: String(complex.id),
      });
      setBuilding(created);
      setBuildingSuccess('Дом добавлен');
      showSoftSuccess('Дом добавлен');
      markCompleted(1);
      fetchBuildingsByComplexFx(String(complex.id)).catch(() => {});
      if (isSettlementTenant || kind === 'settlement' || complex?.complex_kind === 'settlement') {
        try {
          await buildingCorpsApi.ensureDefault(String(created.id));
          setCorpsSuccess('Готово без корпусов');
          markCompleted(2);
          setActiveStep(3);
          fetchCorpsByBuildingFx(String(created.id)).catch(() => {});
        } catch (corpsErr) {
          setActiveStep(2);
        }
      } else {
        setActiveStep(2);
      }
    } catch (err) {
      setBuildingError(
        err?.response?.data?.detail ||
          err?.message ||
          'Не удалось создать дом'
      );
    } finally {
      setBuildingLoading(false);
    }
  };

  const handleAddCorps = () => {
    const value = corpsDraft.trim();
    if (!value) return;
    setCorpsList((prev) =>
      prev.includes(value) ? prev : [...prev, value]
    );
    setCorpsDraft('');
  };

  const handleRemoveCorps = (name) => {
    setCorpsList((prev) => prev.filter((item) => item !== name));
  };

  const handleCreateCorps = async () => {
    if (!building?.id) {
      setCorpsError('Сначала создайте дом');
      return;
    }

    const normalized = corpsList.length
      ? corpsList
      : corpsDraft.trim()
      ? [corpsDraft.trim()]
      : [];

    setCorpsLoading(true);
    setCorpsError('');
    setCorpsSuccess('');
    try {
      if (!normalized.length) {
        const item = await buildingCorpsApi.ensureDefault(String(building.id));
        setCreatedCorps([item]);
        setCorpsSuccess('Готово без корпусов');
        showSoftSuccess('Готово');
      } else {
        const created = [];
        for (const name of normalized) {
          const item = await buildingCorpsApi.create({
            name,
            building_id: String(building.id),
          });
          created.push(item);
        }
        setCreatedCorps(created);
        setCorpsList(normalized);
        setCorpsSuccess('Корпуса добавлены');
        showSoftSuccess('Корпуса добавлены');
      }
      markCompleted(2);
      setActiveStep(3);
      fetchCorpsByBuildingFx(String(building.id)).catch(() => {});
    } catch (err) {
      setCorpsError(
        err?.response?.data?.detail ||
          err?.message ||
          'Не удалось создать корпуса'
      );
    } finally {
      setCorpsLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setBulkError('');
    try {
      const blob = await bulkApi.downloadTemplate();
      downloadBlobAsFile(blob, 'bulk_template.xlsx');
    } catch (err) {
      setBulkError(
        err?.response?.data?.detail ||
          err?.message ||
          'Не удалось скачать шаблон'
      );
    }
  };

  const handlePreviewFile = async (file) => {
    setBulkPreviewLoading(true);
    setBulkError('');
    setBulkPreview(null);
    setBulkSuccess('');
    try {
      const preview = await bulkApi.previewFile(file, { offset: 0, limit: 200 });
      setBulkPreview(preview);
    } catch (err) {
      setBulkError(
        err?.response?.data?.detail ||
          err?.message ||
          'Не удалось проверить файл'
      );
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  const handleImportFile = async () => {
    if (!bulkFile) return;
    setBulkImportLoading(true);
    setBulkError('');
    setBulkSuccess('');
    try {
      const result = await bulkApi.uploadFile(bulkFile);
      const c = result.created || {};
      setBulkSuccess(
        `Готово. ЖК ${c.residential_complexes || 0}, домов ${c.buildings || 0}, квартир ${c.apartments || 0}`
      );
      showSoftSuccess('Импорт готов');
      setBulkSkipped(false);
      markCompleted(3);
    } catch (err) {
      setBulkError(
        err?.response?.data?.detail ||
          err?.message ||
          'Не удалось импортировать файл'
      );
    } finally {
      setBulkImportLoading(false);
    }
  };

  const handleSkipImport = () => {
    setBulkSkipped(true);
    markCompleted(3);
    setActiveStep(4);
  };

  const loadGateways = useCallback(async () => {
    if (!complex?.id) return;
    setGatewaysLoading(true);
    setGatewaysError('');
    try {
      const data = await gatewaysApi.list({
        residential_complex_id: String(complex.id),
        building_id: building?.id ? String(building.id) : undefined,
      });
      setGateways(data || []);
    } catch (err) {
      setGatewaysError(
        err?.response?.data?.detail ||
          err?.message ||
          'Не удалось загрузить шлюзы'
      );
    } finally {
      setGatewaysLoading(false);
    }
  }, [complex?.id, building?.id]);

  useEffect(() => {
    if (activeStep === 4) {
      loadGateways();
    }
  }, [activeStep, loadGateways]);

  const previewStats = useMemo(() => {
    if (!bulkPreview) return null;
    const rows = bulkPreview.rows || [];
    const rowsWithErrors = rows.filter((row) => row.row_errors?.length).length;
    return {
      total: bulkPreview.total_rows || rows.length,
      rowsWithErrors,
      errors: bulkPreview.errors?.length || 0,
    };
  }, [bulkPreview]);

  const currentCorpsName = useMemo(() => {
    if (corpsList.length) return corpsList[0];
    if (createdCorps.length) return createdCorps[0]?.name;
    return '';
  }, [corpsList, createdCorps]);

  return (
    <Box
      className="home-container page-animate"
      sx={{
        px: { xs: 2, md: 3 },
        py: 3,
        maxWidth: 1200,
        mx: 'auto',
        width: '100%',
      }}
    >
      <Paper
        className="glass-sheen"
        sx={{
          p: { xs: 2.5, md: 4 },
          borderRadius: 4,
          mb: 3,
          background: 'var(--surface-bg)',
          border: (theme) =>
            theme.palette.mode === 'dark'
              ? '1px solid rgba(148, 163, 184, 0.18)'
              : '1px solid rgba(15, 23, 42, 0.08)',
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(400px 220px at 20% 0%, rgba(37, 99, 235, 0.18), transparent 70%)',
            opacity: (theme) => (theme.palette.mode === 'dark' ? 0.35 : 0.2),
            pointerEvents: 'none',
          },
        }}
      >
        <Stack spacing={1.2} sx={{ position: 'relative', zIndex: 1 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Быстрый старт
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
                Мастер первичной настройки: создайте объект, добавьте корпуса,
                загрузите устройства и убедитесь, что связь со шлюзами стабильна.
              </Typography>
            </Box>
            {showDismiss && (
              <Button
                variant="text"
                color="inherit"
                onClick={onDismiss}
                sx={{ fontWeight: 600 }}
              >
                Скрыть
              </Button>
            )}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <Chip
              icon={<CheckCircleRoundedIcon />}
              label={`Готово шагов: ${completedCount}/${totalSteps}`}
              color={completedCount === totalSteps ? 'success' : 'default'}
              sx={{ fontWeight: 600 }}
            />
            <Box sx={{ flex: 1, width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={stepProgress}
                sx={{
                  height: 8,
                  borderRadius: 999,
                  backgroundColor: 'rgba(148, 163, 184, 0.24)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                  },
                }}
              />
            </Box>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 4 }}>
        <Stepper
          nonLinear
          activeStep={activeStep}
          sx={{
            '& .MuiStepLabel-label': { fontWeight: 600 },
            '& .MuiStepButton-root': { borderRadius: 2 },
          }}
        >
          {steps.map((step, index) => (
            <Step key={step.key} completed={completed[index]}>
              <StepButton
                color="inherit"
                onClick={() => handleStep(index)}
                disabled={!canStepTo(index)}
              >
                {step.label}
              </StepButton>
            </Step>
          ))}
        </Stepper>

        <Divider sx={{ my: 3 }} />

        {activeStep === 0 && (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ApartmentRoundedIcon color="primary" />
              <Typography variant="h6">
                {isSettlementTenant ? 'Шаг 1. Создайте посёлок' : 'Шаг 1. Создайте жилой комплекс'}
              </Typography>
              <Chip
                size="small"
                label={kindLabel}
                color={isSettlementTenant ? 'success' : 'default'}
                variant="outlined"
              />
            </Stack>
            <Typography color="text.secondary">
              {isSettlementTenant
                ? 'Заполните название и адрес посёлка. Тип задан при регистрации клиента.'
                : 'Заполните название и адрес ЖК. Тип задан при регистрации клиента.'}
            </Typography>
            {complexError && <Alert severity="error">{complexError}</Alert>}
            {complexSuccess && <Alert severity="success">{complexSuccess}</Alert>}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label={isSettlementTenant ? 'Название посёлка' : 'Название ЖК'}
                value={complexForm.name}
                onChange={(e) => {
                  const value = e.target.value;
                  setComplexForm((prev) => ({ ...prev, name: value }));
                  setComplexErrors((prev) => ({ ...prev, name: value.trim() ? '' : prev.name }));
                }}
                fullWidth
                error={Boolean(complexErrors.name)}
                helperText={complexErrors.name}
              />
              <AddressAutocompleteField
                label="Адрес"
                value={complexForm.address}
                onValueChange={(value) => {
                  setComplexForm((prev) => ({
                    ...prev,
                    address: value,
                    latitude: null,
                    longitude: null,
                  }));
                  setComplexErrors((prev) => ({ ...prev, address: value.trim() ? '' : prev.address }));
                }}
                onSuggestionSelect={(suggestion) => {
                  const lat = Number(suggestion?.data?.geo_lat);
                  const lon = Number(suggestion?.data?.geo_lon);
                  setComplexForm((prev) => ({
                    ...prev,
                    address: suggestion?.value || prev.address,
                    latitude: Number.isFinite(lat) ? lat : null,
                    longitude: Number.isFinite(lon) ? lon : null,
                  }));
                  setComplexErrors((prev) => ({ ...prev, address: '' }));
                }}
                required
                error={Boolean(complexErrors.address)}
                helperText={complexErrors.address}
              />
            </Stack>
            {complex && (
              <Alert severity="info">
                Активный ЖК: {complex.name} • {complex.address}
              </Alert>
            )}
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                onClick={handleCreateComplex}
                disabled={complexLoading}
              >
                {complexLoading
                  ? 'Создаём…'
                  : isSettlementTenant
                    ? 'Создать посёлок'
                    : 'Создать ЖК'}
              </Button>
              <Button
                variant="text"
                onClick={handleNext}
                disabled={!completed[0]}
                endIcon={<ArrowForwardRoundedIcon />}
              >
                Продолжить
              </Button>
            </Stack>
          </Stack>
        )}

        {activeStep === 1 && (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <HomeWorkRoundedIcon color="primary" />
              <Typography variant="h6">Шаг 2. Добавьте дом</Typography>
            </Stack>
            <Typography color="text.secondary">
              Укажите название дома и при необходимости адрес. Дом будет привязан к ЖК.
            </Typography>
            {!complex && (
              <Alert severity="warning">
                Сначала создайте ЖК, чтобы привязать дом.
              </Alert>
            )}
            {buildingError && <Alert severity="error">{buildingError}</Alert>}
            {buildingSuccess && <Alert severity="success">{buildingSuccess}</Alert>}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Название дома"
                value={buildingForm.name}
                onChange={(e) => {
                  const value = e.target.value;
                  setBuildingForm((prev) => ({ ...prev, name: value }));
                  setBuildingErrors((prev) => ({ ...prev, name: value.trim() ? '' : prev.name }));
                }}
                fullWidth
                error={Boolean(buildingErrors.name)}
                helperText={buildingErrors.name}
                disabled={!complex}
              />
              <AddressAutocompleteField
                label="Адрес (опционально)"
                value={buildingForm.address}
                onValueChange={(value) =>
                  setBuildingForm((prev) => ({
                    ...prev,
                    address: value,
                    latitude: null,
                    longitude: null,
                  }))
                }
                onSuggestionSelect={(suggestion) => {
                  const lat = Number(suggestion?.data?.geo_lat);
                  const lon = Number(suggestion?.data?.geo_lon);
                  setBuildingForm((prev) => ({
                    ...prev,
                    address: suggestion?.value || prev.address,
                    latitude: Number.isFinite(lat) ? lat : null,
                    longitude: Number.isFinite(lon) ? lon : null,
                  }));
                }}
                disabled={!complex}
              />
            </Stack>
            {building && (
              <Alert severity="info">
                Дом создан: {building.name}
                {building.address ? ` • ${building.address}` : ''}
              </Alert>
            )}
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                Назад
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateBuilding}
                disabled={buildingLoading || !complex}
              >
                {buildingLoading ? 'Создаём…' : 'Создать дом'}
              </Button>
              <Button
                variant="text"
                onClick={handleNext}
                disabled={!completed[1]}
                endIcon={<ArrowForwardRoundedIcon />}
              >
                Продолжить
              </Button>
            </Stack>
          </Stack>
        )}

        {activeStep === 2 && (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccountTreeRoundedIcon color="primary" />
              <Typography variant="h6">
                {isSettlementTenant ? 'Шаг 3. Участки без корпусов' : 'Шаг 3. Создайте корпуса'}
              </Typography>
            </Stack>
            <Typography color="text.secondary">{labels.corpsSkipHint}</Typography>
            {isSettlementTenant ? (
              <Alert severity="info">
                Корпуса для посёлка не создаются. Нажмите «Продолжить» и загрузите дома с номерами участков.
              </Alert>
            ) : null}
            {!building && !isSettlementTenant && (
              <Alert severity="warning">
                Сначала создайте дом, чтобы добавить корпуса.
              </Alert>
            )}
            {corpsError && <Alert severity="error">{corpsError}</Alert>}
            {corpsSuccess && <Alert severity="success">{corpsSuccess}</Alert>}
            {!isSettlementTenant && (
              <>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Название корпуса"
                    value={corpsDraft}
                    onChange={(e) => setCorpsDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCorps();
                      }
                    }}
                    fullWidth
                    disabled={!building}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddCorps}
                    disabled={!building}
                  >
                    Добавить корпус
                  </Button>
                </Stack>
                {corpsList.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {corpsList.map((name) => (
                      <Chip
                        key={name}
                        label={name}
                        onDelete={() => handleRemoveCorps(name)}
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                )}
                {createdCorps.length > 0 && (
                  <Alert severity="info">
                    Создано корпусов: {createdCorps.length}. Первый корпус:{' '}
                    {createdCorps[0]?.name || '—'}.
                  </Alert>
                )}
              </>
            )}
            <Stack direction="row" spacing={1.5}>
              <Button variant="outlined" onClick={handleBack}>
                Назад
              </Button>
              {isSettlementTenant ? (
                <Button
                  variant="contained"
                  onClick={() => {
                    markCompleted(2);
                    setActiveStep(3);
                  }}
                  disabled={!building && !completed[1]}
                  endIcon={<ArrowForwardRoundedIcon />}
                >
                  К импорту участков
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleCreateCorps}
                  disabled={corpsLoading || !building}
                >
                  {corpsLoading
                    ? 'Сохраняем…'
                    : (corpsList.length || corpsDraft.trim())
                      ? 'Создать корпуса'
                      : 'Продолжить без корпусов'}
                </Button>
              )}
              <Button
                variant="text"
                onClick={handleNext}
                disabled={!completed[2]}
                endIcon={<ArrowForwardRoundedIcon />}
              >
                Продолжить
              </Button>
            </Stack>
          </Stack>
        )}

        {activeStep === 3 && (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CloudUploadRoundedIcon color="primary" />
              <Typography variant="h6">
                {isSettlementTenant
                  ? 'Шаг 4. Импорт домов и участков'
                  : 'Шаг 4. Импорт устройств и помещений'}
              </Typography>
            </Stack>
            <Typography color="text.secondary">
              Используйте шаблон импорта, чтобы создать квартиры и устройства.
              Укажите ЖК и дом; корпус — только если он реально есть, иначе оставьте пустым.
              Для посёлка корпус пустой, квартира = номер участка.
            </Typography>
            {bulkError && <Alert severity="error">{bulkError}</Alert>}
            {bulkSuccess && <Alert severity="success">{bulkSuccess}</Alert>}
            {bulkSkipped && (
              <Alert severity="info">Импорт пропущен. Можно вернуться позже.</Alert>
            )}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                borderStyle: 'dashed',
                backgroundColor: 'rgba(148, 163, 184, 0.06)',
              }}
            >
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    variant="outlined"
                    startIcon={<CloudDownloadRoundedIcon />}
                    onClick={handleDownloadTemplate}
                  >
                    Скачать шаблон
                  </Button>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUploadRoundedIcon />}
                    disabled={bulkPreviewLoading}
                  >
                    Выбрать файл
                    <input
                      type="file"
                      hidden
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setBulkFile(file);
                          handlePreviewFile(file);
                        }
                      }}
                    />
                  </Button>
                </Stack>
                {bulkFile && (
                  <Typography variant="body2">
                    Файл: <strong>{bulkFile.name}</strong>
                  </Typography>
                )}
                {bulkPreviewLoading && <LinearProgress />}
                {previewStats && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
                    <Chip label={`Строк в файле: ${previewStats.total}`} />
                    <Chip label={`Ошибочные строки: ${previewStats.rowsWithErrors}`} />
                    <Chip label={`Системные ошибки: ${previewStats.errors}`} />
                  </Stack>
                )}
                {previewStats && (previewStats.rowsWithErrors > 0 || previewStats.errors > 0) && (
                  <Alert severity="warning">
                    В файле есть ошибки. Откройте расширенный импорт.
                  </Alert>
                )}
              </Stack>
            </Paper>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap">
              {complex?.name && <Chip color="primary" label={`ЖК: ${complex.name}`} />}
              {building?.name && <Chip color="primary" label={`Дом: ${building.name}`} />}
              {currentCorpsName && currentCorpsName !== 'Без корпуса' && (
                <Chip color="primary" label={`Корпус: ${currentCorpsName}`} />
              )}
              {building?.name && (!currentCorpsName || currentCorpsName === 'Без корпуса') && (
                <Chip variant="outlined" label="Без корпусов" />
              )}
            </Stack>
            <Alert severity="info">
              Назначение помещений выполняется через колонки apartment_* в шаблоне.
              При необходимости вы сможете позже вручную добавить квартиры и устройства.
            </Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="outlined" onClick={handleBack}>
                Назад
              </Button>
              <Button
                variant="contained"
                onClick={handleImportFile}
                disabled={!bulkFile || bulkImportLoading}
              >
                {bulkImportLoading ? 'Импортируем…' : 'Импортировать'}
              </Button>
              <Button variant="text" onClick={handleSkipImport}>
                Пропустить
              </Button>
              <Button variant="text" onClick={() => navigate('/admin/add-complex?mode=import')}>
                Расширенный импорт
              </Button>
              {building?.id && (
                <Button
                  variant="text"
                  onClick={() => navigate(`/building/${building.id}/corps`)}
                >
                  Ручное назначение
                </Button>
              )}
              <Button
                variant="text"
                onClick={handleNext}
                disabled={!completed[3]}
                endIcon={<ArrowForwardRoundedIcon />}
              >
                Продолжить
              </Button>
            </Stack>
          </Stack>
        )}

        {activeStep === 4 && (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <RouterRoundedIcon color="primary" />
              <Typography variant="h6">Шаг 5. Проверка связи шлюзов</Typography>
            </Stack>
            <Typography color="text.secondary">
              Убедитесь, что шлюзы передают данные. При проблемах можно перейти в раздел управления.
            </Typography>
            {gatewaysError && <Alert severity="error">{gatewaysError}</Alert>}
            {gatewaysLoading && <LinearProgress />}
            {!gatewaysLoading && gateways.length === 0 && (
              <Alert severity="warning">
                Шлюзы не найдены. Добавьте их вручную в разделе «Шлюзы».
              </Alert>
            )}
            {gateways.length > 0 && (
              <Stack spacing={1.5}>
                {gateways.map((gateway) => (
                  <Paper
                    key={gateway.id}
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 3 }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        justifyContent="space-between"
                      >
                        <Typography sx={{ fontWeight: 600 }}>
                          {gateway.name || 'Без названия'}
                        </Typography>
                        <Chip
                          size="small"
                          color={statusColor(gateway.status)}
                          label={gateway.status || '—'}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Последний сигнал: {formatDate(gateway.last_seen_at)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Host: {gateway.host || '—'} • MQTT: {gateway.mqtt_topic || '—'}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="outlined" onClick={handleBack}>
                Назад
              </Button>
              <Tooltip title="Обновить статусы">
                <Button
                  variant="outlined"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={loadGateways}
                  disabled={gatewaysLoading}
                >
                  Обновить
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                onClick={() => {
                  markCompleted(4);
                  setActiveStep(5);
                  onComplete?.();
                }}
              >
                Завершить
              </Button>
              <Button variant="text" onClick={() => navigate('/gateways')}>
                Перейти к шлюзам
              </Button>
            </Stack>
          </Stack>
        )}

        {activeStep >= totalSteps && (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleRoundedIcon color="success" />
              <Typography variant="h6">Онбординг завершён</Typography>
            </Stack>
            <Typography color="text.secondary">
              Вы можете продолжить настройку данных или перейти к объектам.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {complex?.id && (
                <Button
                  variant="contained"
                  onClick={() => navigate(`/complex/${complex.id}`)}
                >
                  Открыть ЖК
                </Button>
              )}
              {building?.id && (
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/building/${building.id}/corps`)}
                >
                  Перейти к корпусам
                </Button>
              )}
              {showDismiss && (
                <Button variant="outlined" onClick={onDismiss}>
                  Закрыть
                </Button>
              )}
              <Button variant="text" onClick={() => navigate('/home')}>
                На главную
              </Button>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default OnboardingPage;
